import { inQuietHours, quietHoursEnd } from './time'

/**
 * The customer's own time zone for quiet hours (design 6.2, 14). In order:
 * a stored person time zone, then their address (state, country or
 * Australian postcode), then their phone number's country and area, then the
 * company's. When a phone number only tells us the country and that country
 * spans several zones (an Australian or US mobile), every candidate zone is
 * returned and a message waits until it is outside quiet hours in all of
 * them, so nobody in Perth is texted at 06:00 because the company is in
 * Sydney.
 */
export interface CustomerZones { zones: string[]; source: 'person' | 'address' | 'phone' | 'company' }

export function validZone(tz: string | null | undefined): tz is string {
  if (!tz) return false
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); return true } catch { return false }
}

const AU_STATES: Record<string, string> = {
  NSW: 'Australia/Sydney', ACT: 'Australia/Sydney', VIC: 'Australia/Melbourne', TAS: 'Australia/Hobart', QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide', NT: 'Australia/Darwin', WA: 'Australia/Perth',
  'NEW SOUTH WALES': 'Australia/Sydney', 'AUSTRALIAN CAPITAL TERRITORY': 'Australia/Sydney', VICTORIA: 'Australia/Melbourne', TASMANIA: 'Australia/Hobart',
  QUEENSLAND: 'Australia/Brisbane', 'SOUTH AUSTRALIA': 'Australia/Adelaide', 'NORTHERN TERRITORY': 'Australia/Darwin', 'WESTERN AUSTRALIA': 'Australia/Perth',
}

/** Countries with one zone, and the candidate zones of the larger ones (mainland, populated). */
const COUNTRY_ZONES: Record<string, string[]> = {
  AU: ['Australia/Sydney', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Perth'],
  NZ: ['Pacific/Auckland'], GB: ['Europe/London'], IE: ['Europe/Dublin'], SG: ['Asia/Singapore'], HK: ['Asia/Hong_Kong'], IN: ['Asia/Kolkata'],
  ZA: ['Africa/Johannesburg'], FR: ['Europe/Paris'], DE: ['Europe/Berlin'], ES: ['Europe/Madrid'], IT: ['Europe/Rome'], NL: ['Europe/Amsterdam'],
  BE: ['Europe/Brussels'], CH: ['Europe/Zurich'], AT: ['Europe/Vienna'], SE: ['Europe/Stockholm'], NO: ['Europe/Oslo'], DK: ['Europe/Copenhagen'],
  FI: ['Europe/Helsinki'], PL: ['Europe/Warsaw'], PT: ['Europe/Lisbon'], GR: ['Europe/Athens'], AE: ['Asia/Dubai'], JP: ['Asia/Tokyo'],
  KR: ['Asia/Seoul'], CN: ['Asia/Shanghai'], PH: ['Asia/Manila'], MY: ['Asia/Kuala_Lumpur'], TH: ['Asia/Bangkok'], VN: ['Asia/Ho_Chi_Minh'],
  FJ: ['Pacific/Fiji'], IL: ['Asia/Jerusalem'],
  US: ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'],
  CA: ['America/Halifax', 'America/Toronto', 'America/Winnipeg', 'America/Edmonton', 'America/Vancouver'],
}
const COUNTRY_NAMES: Record<string, string> = { AUSTRALIA: 'AU', 'NEW ZEALAND': 'NZ', 'UNITED KINGDOM': 'GB', UK: 'GB', ENGLAND: 'GB', SCOTLAND: 'GB', WALES: 'GB', IRELAND: 'IE', USA: 'US', 'UNITED STATES': 'US', CANADA: 'CA', SINGAPORE: 'SG' }

/** Dial codes, longest first. +1 is shared by the US and Canada. */
const DIAL: [string, string][] = ([
  ['61', 'AU'], ['64', 'NZ'], ['44', 'GB'], ['353', 'IE'], ['65', 'SG'], ['852', 'HK'], ['91', 'IN'], ['27', 'ZA'], ['33', 'FR'], ['49', 'DE'],
  ['34', 'ES'], ['39', 'IT'], ['31', 'NL'], ['32', 'BE'], ['41', 'CH'], ['43', 'AT'], ['46', 'SE'], ['47', 'NO'], ['45', 'DK'], ['358', 'FI'],
  ['48', 'PL'], ['351', 'PT'], ['30', 'GR'], ['971', 'AE'], ['81', 'JP'], ['82', 'KR'], ['86', 'CN'], ['63', 'PH'], ['60', 'MY'], ['66', 'TH'],
  ['84', 'VN'], ['679', 'FJ'], ['972', 'IL'], ['1', 'US'],
] as [string, string][]).sort((a, b) => b[0].length - a[0].length)

export function phoneCountry(e164: string | null | undefined): string | null {
  const d = String(e164 ?? '').replace(/^\+/, '')
  if (!/^\d{6,15}$/.test(d)) return null
  return DIAL.find(([code]) => d.startsWith(code))?.[1] ?? null
}

/** Australian landlines carry their area: 02 Sydney, 03 Melbourne, 07 Brisbane. 08 spans three zones; mobiles say nothing. */
function auPhoneZones(e164: string): string[] | null {
  const area = e164.replace(/^\+61/, '')[0]
  if (area === '2') return ['Australia/Sydney']
  if (area === '3') return ['Australia/Melbourne']
  if (area === '7') return ['Australia/Brisbane']
  if (area === '8') return ['Australia/Adelaide', 'Australia/Darwin', 'Australia/Perth']
  return null
}

function auPostcodeZone(pc: string): string | null {
  const n = Number(pc)
  if (!/^\d{4}$/.test(pc)) return null
  if (n >= 800 && n <= 999) return 'Australia/Darwin'
  if (n >= 2000 && n <= 2999) return n === 2880 ? 'Australia/Broken_Hill' : 'Australia/Sydney'
  if (n >= 3000 && n <= 3999) return 'Australia/Melbourne'
  if (n >= 4000 && n <= 4999) return 'Australia/Brisbane'
  if (n >= 5000 && n <= 5999) return 'Australia/Adelaide'
  if (n >= 6000 && n <= 6999) return 'Australia/Perth'
  if (n >= 7000 && n <= 7999) return 'Australia/Hobart'
  return null
}

export function customerZones(i: {
  person_tz?: string | null
  phones?: (string | null | undefined)[]
  address?: { state?: unknown; region?: unknown; country?: unknown; postcode?: unknown } | null
  company_tz: string
  company_country: string
}): CustomerZones {
  if (validZone(i.person_tz)) return { zones: [i.person_tz], source: 'person' }
  const company = { zones: [validZone(i.company_tz) ? i.company_tz : 'UTC'], source: 'company' as const }
  const a = i.address ?? {}
  const country = (() => {
    const c = String(a.country ?? '').trim().toUpperCase()
    if (!c) return null
    return COUNTRY_NAMES[c] ?? (c.length === 2 ? c : null)
  })()
  const auAddress = country === 'AU' || (!country && i.company_country === 'AU')
  const state = String(a.state ?? a.region ?? '').trim().toUpperCase()
  if (state && auAddress && AU_STATES[state]) return { zones: [AU_STATES[state]], source: 'address' }
  const pc = String(a.postcode ?? '').trim()
  if (pc && auAddress) { const z = auPostcodeZone(pc.padStart(4, '0')); if (z) return { zones: [z], source: 'address' } }
  if (country && COUNTRY_ZONES[country]?.length === 1) return { zones: COUNTRY_ZONES[country], source: 'address' }

  // Phones: the candidate zones of every number we may text, so the strictest window wins.
  const zones = new Set<string>()
  for (const p of i.phones ?? []) {
    const c = phoneCountry(p)
    if (!c) continue
    const byArea = c === 'AU' ? auPhoneZones(String(p)) : null
    for (const z of byArea ?? COUNTRY_ZONES[c] ?? []) zones.add(z)
  }
  if (zones.size) return { zones: [...zones], source: 'phone' }
  if (country && COUNTRY_ZONES[country]) return { zones: COUNTRY_ZONES[country], source: 'address' }
  return company
}

/**
 * The first instant at or after `at` that is outside quiet hours in every zone. If the zones are so far
 * apart that no such instant exists within two days, the first zone decides (it is never "now" in quiet hours there).
 */
export function quietHoursEndAll(at: Date, quiet: { start: string; end: string }, zones: string[]): Date {
  if (!zones.length) return at
  let t = at
  for (let i = 0; i < 16; i++) {
    const blocked = zones.find((z) => inQuietHours(t, quiet, z))
    if (!blocked) return t
    t = quietHoursEnd(t, quiet, blocked)
    if (t.getTime() - at.getTime() > 2 * 86_400_000) break
  }
  return quietHoursEnd(at, quiet, zones[0])
}
